export const AUTH_ACCESS_TOKEN_STORAGE_KEY = "auth_access_token";

const LEGACY_PLANNER_TOKEN_KEY = "planner_access_token";

/**
 * Kiosk offline-session storage keys (GitLab #94 — DriverRoom TV).
 *
 * These are DISTINCT from the normal access-token key on purpose: they only ever
 * hold the long-lived `offline_access` refresh token (+ optional id token) for a
 * kiosk browser. A browser that has never been a kiosk never has these keys, so
 * their presence is the pre-login "this browser is the kiosk" signal. Logout wipes
 * them so the next visitor is a normal user again.
 */
export const KIOSK_OFFLINE_REFRESH_TOKEN_KEY = "kiosk_offline_refresh_token";
export const KIOSK_OFFLINE_ID_TOKEN_KEY = "kiosk_offline_id_token";

export interface StoredKioskOfflineToken {
  refreshToken: string;
  idToken?: string;
}

export function getStoredAccessToken(): string | undefined {
  try {
    const next = window.localStorage
      .getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
      ?.trim();
    if (next) return next;
    const legacy = window.localStorage
      .getItem(LEGACY_PLANNER_TOKEN_KEY)
      ?.trim();
    if (legacy) {
      setStoredAccessToken(legacy);
      window.localStorage.removeItem(LEGACY_PLANNER_TOKEN_KEY);
      return legacy;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function setStoredAccessToken(token: string): void {
  try {
    window.localStorage.setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, token.trim());
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStoredAccessToken(): void {
  try {
    window.localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_PLANNER_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Reads the persisted kiosk offline refresh token (+ optional id token). Returns
 * null when no kiosk offline session has been stored on this browser.
 */
export function getStoredKioskOfflineToken(): StoredKioskOfflineToken | null {
  try {
    const refreshToken = window.localStorage
      .getItem(KIOSK_OFFLINE_REFRESH_TOKEN_KEY)
      ?.trim();
    if (!refreshToken) return null;
    const idToken = window.localStorage
      .getItem(KIOSK_OFFLINE_ID_TOKEN_KEY)
      ?.trim();
    return { refreshToken, idToken: idToken || undefined };
  } catch {
    return null;
  }
}

/** Persists the kiosk offline refresh token (+ optional id token). No-ops on empty input. */
export function setStoredKioskOfflineToken(
  refreshToken: string,
  idToken?: string,
): void {
  try {
    const trimmed = refreshToken.trim();
    if (!trimmed) return;
    window.localStorage.setItem(KIOSK_OFFLINE_REFRESH_TOKEN_KEY, trimmed);
    const trimmedId = idToken?.trim();
    if (trimmedId) {
      window.localStorage.setItem(KIOSK_OFFLINE_ID_TOKEN_KEY, trimmedId);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

/** Wipes the kiosk offline session so the next visitor gets the normal login screen. */
export function clearStoredKioskOffline(): void {
  try {
    window.localStorage.removeItem(KIOSK_OFFLINE_REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(KIOSK_OFFLINE_ID_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
